import { ClaimStateChangeSource, WriteToolName } from '@monolog/shared/types/automation.types';
import { ClaimPauseReason, ResolutionType } from '@monolog/shared/types/database/claims-svc';

/** String form of the enum, so the same constants type-check against Prisma's union and the typegen enum. */
export type ClaimPauseReasonCode = keyof typeof ClaimPauseReason;

/** Values of the `claim-automation-processor-owner-be` flag; `Both` is the overlap every flip passes through. */
export const ClaimAutomationProcessorOwner = {
  ClaimsSvc: 'claims-svc',
  Both: 'both',
  ClaimAutomationSvc: 'claim-automation-svc',
} as const;
export type ClaimAutomationProcessorOwner =
  (typeof ClaimAutomationProcessorOwner)[keyof typeof ClaimAutomationProcessorOwner];

/** The services that can process claim automation work. */
export type ClaimAutomationProcessorService =
  | typeof ClaimAutomationProcessorOwner.ClaimsSvc
  | typeof ClaimAutomationProcessorOwner.ClaimAutomationSvc;

/** An absent or unreachable owner flag resolves here, so automation keeps running in claims-svc. */
export const DEFAULT_CLAIM_AUTOMATION_PROCESSOR_OWNER: ClaimAutomationProcessorOwner =
  ClaimAutomationProcessorOwner.ClaimsSvc;

const PROCESSES_CLAIM_AUTOMATION: Record<ClaimAutomationProcessorService, (value: unknown) => boolean> = {
  [ClaimAutomationProcessorOwner.ClaimsSvc]: value => value !== ClaimAutomationProcessorOwner.ClaimAutomationSvc,
  [ClaimAutomationProcessorOwner.ClaimAutomationSvc]: value =>
    value === ClaimAutomationProcessorOwner.Both || value === ClaimAutomationProcessorOwner.ClaimAutomationSvc,
};

/** Unknown flag values mean claims-svc only, so a typo'd or non-string value can never idle both services. */
export function processesClaimAutomation(service: ClaimAutomationProcessorService, value: unknown): boolean {
  return PROCESSES_CLAIM_AUTOMATION[service](value);
}

/** Prefix used on internal notes created by AI automation escalation */
export const AI_ESCALATION_PREFIX = '[AI Escalation]';

/**
 * Prefix on the internal note an agent writes when handing a takeover back to the AI early. It is
 * the human's direction for the run the hand-back starts; the AI reads it through `get_order_notes`.
 */
export const AI_HANDBACK_NOTE_PREFIX = 'AI Hand Back:';

/** Bounds on a hand-back note: long enough to say what changed, short enough to read at a glance. */
export const AI_HANDBACK_NOTE_MIN_LENGTH = 20;
export const AI_HANDBACK_NOTE_MAX_LENGTH = 2000;

/**
 * State changes whose cause already decided whether to run automation, so the STATE_CHANGE
 * they emit would only duplicate that decision. These include transitions automation made to
 * itself, a manual brand hold (automation is paused), and the flip `updateStateBasedOnMessage`
 * makes in response to a message — `claim_message_created` owns the trigger for that message
 * regardless of who sent it. `automation.event.controller`'s `claim_state_updated` handler
 * skips every source listed here, so a new such transition only needs an entry added.
 */
export const SUPPRESSED_STATE_CHANGE_SOURCES: ReadonlySet<ClaimStateChangeSource> = new Set([
  ClaimStateChangeSource.AUTOMATION_WAIT,
  ClaimStateChangeSource.AUTOMATION_EXECUTION_FAILURE,
  ClaimStateChangeSource.AUTOMATION_ESCALATION,
  ClaimStateChangeSource.MANUAL_HOLD_FOR_BRAND,
  ClaimStateChangeSource.MESSAGE,
]);

/** Order note left when a resolve's order tag could not be applied, so a human can add it by hand. */
export const AI_ORDER_TAG_FAILED_PREFIX = '[AI Order Tag Failed]';

/** Claim note the fraud check leaves on medium/high claims; the run that follows already reads the report. */
export const FRAUD_CHECK_NOTE_PREFIX = '[Fraud Check]';

/**
 * Note prefixes the `note_created` handler skips before any claim lookup: a note the agent just
 * wrote is not new context, and a hand-back note's writer starts the run itself.
 */
export const SELF_AUTHORED_NOTE_PREFIXES: readonly string[] = [
  AI_ESCALATION_PREFIX,
  AI_HANDBACK_NOTE_PREFIX,
  AI_ORDER_TAG_FAILED_PREFIX,
  FRAUD_CHECK_NOTE_PREFIX,
];

/** Username automated notes are posted under: the claims-svc fraud check, and historically the Yofi webhook. */
export const OP_BOT_NOTE_AUTHOR = 'OrderProtection Bot';

/** Note authors that are bots, not people; their notes are held while a claim is Pending on the customer. */
export const SYSTEM_NOTE_AUTHORS: ReadonlySet<string> = new Set([OP_BOT_NOTE_AUTHOR]);

/** Display names for `AIProvider` model values, shared by Valet and the claims-svc filter configs. */
/** Whether a model is a gateway slug (`<vendor>/<model>`) rather than one of our ids, which never contain a slash. */
export function isGatewayModel(model: string): boolean {
  return model.includes('/');
}

export const MODEL_DISPLAY: Record<string, string> = {
  CLAUDE_HAIKU_4_5: 'Claude Haiku 4.5',
  CLAUDE_OPUS_4_6: 'Claude Opus 4.6',
  CLAUDE_SONNET_4_5: 'Claude Sonnet 4.5',
  CLAUDE_SONNET_4_6: 'Claude Sonnet 4.6',
  GEMINI_2_0_FLASH_EXP: 'Gemini 2.0 Flash',
  GEMINI_2_5_FLASH: 'Gemini 2.5 Flash',
  GEMINI_PRO: 'Gemini Pro',
  GPT_4O: 'GPT-4o',
  GPT_4O_MINI: 'GPT-4o Mini',
  GPT_5_5: 'GPT-5.5',
  GPT_5_6_LUNA: 'GPT-5.6 Luna',
  GPT_5_6_TERRA: 'GPT-5.6 Terra',
  KIMI_K2_6: 'Kimi K2.6',
  KIMI_K3: 'Kimi K3',
  BIGQUERY: 'BigQuery',
  GATEWAY: 'Gateway model',
};

/**
 * Prefix on the internal note created (in metadata-svc) when an agent submits
 * an "AI Course Correction" via Valet. The Reject-gate funnel matches on this
 * string to decide whether the Reject button is unlocked, the agent's MCP
 * `get_order_notes` tool recognizes it as authoritative direction, and
 * `automation.event.controller`'s `note_created` handler uses it to dispatch
 * a COURSE_CORRECTION re-trigger with the threshold bypass — keep all readers
 * in lockstep by always referencing this constant.
 */
export const AI_COURSE_CORRECTION_PREFIX = 'AI Course Correction:';

/**
 * Stable user identifier recorded on any action taken autonomously by AI
 * automation (resolution records, state-change events, activity logs, message
 * senders, etc.). The display name varies per store via
 * `ClaimAutomationSettings.aiBotName`; this ID is constant across all stores
 * so AI-driven actions can be queried uniformly downstream.
 */
export const AI_AUTOMATION_USER_ID = 'ai-automation';

/** Default display name used when a store has not configured `aiBotName`. */
export const AI_AUTOMATION_DEFAULT_NAME = 'Trevor W';

/**
 * Actor recorded on pause-ledger events the watchdog sweep writes (nudge, hand-back, reap), so a
 * transition nobody clicked is never attributed to the human who owned the pause.
 */
export const PAUSE_WATCHDOG_ACTOR_ID = 'pause-watchdog';

/** Actor recorded on pause-ledger events written by a platform transition (e.g. the claim closing). */
export const SYSTEM_ACTOR_ID = 'system';

/**
 * Prefix on the internal note written when an agent takes a claim over from the AI, carrying the
 * reason they picked. Teammates read it in the feed; the AI reads it through `get_order_notes` when
 * the watchdog hands the claim back.
 */
export const AI_TAKEOVER_NOTE_PREFIX = 'AI Takeover:';

/** Take-over reasons in the order the modal offers them — the ones with a better primitive first. */
export const PAUSE_REASON_ORDER: readonly ClaimPauseReasonCode[] = [
  ClaimPauseReason.WAITING_ON_BRAND,
  ClaimPauseReason.CUSTOMER_ESCALATION,
  ClaimPauseReason.HANDLING_MANUALLY,
  ClaimPauseReason.AI_ERROR,
  ClaimPauseReason.POLICY_GAP,
  ClaimPauseReason.QA_TESTING,
  ClaimPauseReason.OTHER,
];

export const PAUSE_REASON_LABELS: Record<ClaimPauseReasonCode, string> = {
  [ClaimPauseReason.WAITING_ON_BRAND]: 'Waiting on the brand',
  [ClaimPauseReason.CUSTOMER_ESCALATION]: 'Customer is escalating',
  [ClaimPauseReason.HANDLING_MANUALLY]: 'Handling this one myself',
  [ClaimPauseReason.AI_ERROR]: 'The AI got something wrong',
  [ClaimPauseReason.POLICY_GAP]: 'Policy gap the AI cannot handle',
  [ClaimPauseReason.QA_TESTING]: 'QA / testing',
  [ClaimPauseReason.OTHER]: 'Other',
};

/** Every takeover needs a written reason this long (trimmed); no chip says on its own why the agent stepped in. */
export const PAUSE_REASON_MIN_LENGTH = 10;

/**
 * What the AI is told, per reason, when the watchdog hands a taken-over claim back. Keyed by the
 * reason the human gave so the run does not repeat the thing they paused it for.
 */
export const PAUSE_REASON_HANDBACK_GUIDANCE: Record<ClaimPauseReasonCode, string> = {
  [ClaimPauseReason.WAITING_ON_BRAND]:
    'The agent was waiting on the brand. Do not escalate this claim to support again; if the brand still has not answered, wait or hold it for the brand.',
  [ClaimPauseReason.CUSTOMER_ESCALATION]:
    'The customer was escalating and a human was handling them personally. Prefer waiting unless the customer has replied since the takeover, and never send a templated message that ignores their frustration.',
  [ClaimPauseReason.HANDLING_MANUALLY]:
    'A human was handling this claim by hand. Read the notes and messages since the takeover before acting; if they already told the customer something, do not contradict it.',
  [ClaimPauseReason.AI_ERROR]:
    'The agent paused automation because it got something wrong on this claim. Treat their note as a course correction and do not repeat the earlier action.',
  [ClaimPauseReason.POLICY_GAP]:
    'The agent found a policy gap automation could not handle. Follow their note if it says what to do; otherwise escalate with the specifics rather than guessing.',
  [ClaimPauseReason.QA_TESTING]: 'This claim was paused for internal QA. Proceed as normal.',
  [ClaimPauseReason.OTHER]: "Read the agent's note before acting.",
};

/** Prefix used on internal notes created from Slack escalation thread replies */
export const SLACK_ESCALATION_REPLY_PREFIX = '[Slack Escalation Reply]';

/** Appended to a Slack reply note the action gate judged as chatter, so a reader sees why no run followed. */
export const AI_NO_ACTION_NOTE_MARKER = 'AI: No action needed';

/** Prefix on a verified brand teammate's Slack message on a claim; it lifts a brand hold and can approve a switched-off method. */
export const BRAND_REPLY_NOTE_PREFIX = '[Brand Reply]';

/** Prefix on a verified OrderProtection teammate's Slack message on a claim; it lifts a brand hold but never approves for the brand. */
export const OP_REPLY_NOTE_PREFIX = '[OP Reply]';

const TYPED_SENDER_PREFIX = /^(?:\s*\[(?:brand|op) reply\]\s*)+/i;

/** Drops a typed `[Brand Reply]` / `[OP Reply]` so only dispatch's verified-sender path can start a note with one. */
export function stripSenderPrefixes(content: string): string {
  return content.replace(TYPED_SENDER_PREFIX, '');
}

/** Prefix used on internal notes created from the Valet escalation reply box */
export const ESCALATION_REPLY_PREFIX = '[Escalation Reply]';

/**
 * All prefixes that mark a note as a human reply to an AI escalation, regardless of the surface
 * it was written on. The `note_created` handler matches these to claim-target the re-run via the
 * embedded `(claim:<id>)` tag instead of fanning out to every claim on the order.
 */
export const ESCALATION_REPLY_PREFIXES: readonly string[] = [SLACK_ESCALATION_REPLY_PREFIX, ESCALATION_REPLY_PREFIX];

/**
 * Actions whose auto-execution can be deferred. Drives the per-action delay
 * settings in `ClaimAutomationSettings.autoExecuteDelays`, the merchant-facing
 * settings form, and the Live View feed. Add an entry here to expose a new
 * delay-eligible action — no schema or DTO changes required.
 */
export const DELAYABLE_AUTOMATION_ACTIONS = [
  { key: WriteToolName.RESOLVE_CLAIM, label: 'Resolve claim' },
  { key: WriteToolName.SEND_MESSAGE, label: 'Send message' },
  { key: WriteToolName.UPDATE_SHIPPING_ADDRESS, label: 'Update shipping address' },
  { key: WriteToolName.VALIDATE_OFFLINE_CLAIM, label: 'Validate offline claim' },
] as const;

export type DelayableAutomationAction = (typeof DELAYABLE_AUTOMATION_ACTIONS)[number]['key'];

/**
 * Claim types eligible for per-type automation configuration (auto-execute,
 * monitoring Slack notifications, etc.). Excludes `MULTIPLE` — composite claims
 * never auto-execute, so they never carry per-type config. Drives the
 * `ClaimAutomationNotification` seeding, its DTO `@IsIn` validation, and
 * the merchant-facing settings form. Keep in sync with `Claims.ClaimType`.
 */
export const AUTOMATABLE_CLAIM_TYPES = [
  'STOLEN',
  'DAMAGED',
  'DEFECTIVE_ITEM',
  'ORDER_DELIVERED_NOT_RECEIVED',
  'WRONG_ITEM',
  'MISSING_ITEM',
  'RETURNED_TO_SENDER',
  'TRACKING_NOT_MOVING',
  'WRONG_ADDRESS',
] as const;

export type AutomatableClaimType = (typeof AUTOMATABLE_CLAIM_TYPES)[number];

export type AutoExecuteDelays = Partial<Record<DelayableAutomationAction, number>>;

/** Hard cap on per-action delay (minutes). Keeps scheduled jobs from drifting indefinitely. */
export const MAX_AUTO_EXECUTE_DELAY_MINUTES = 1440;

/** Default per-action delay (minutes) seeded for stores that haven't explicitly configured one. */
export const DEFAULT_AUTO_EXECUTE_DELAY_MINUTES = 10;

/**
 * Cap on queue-size tiers per claim type in `ClaimAutomationSettings.escalationThresholds`
 * (the base ceiling is separate and does not count).
 */
export const MAX_ESCALATION_TIERS_PER_CLAIM_TYPE = 10;

/** Most order tags a store may allow the AI to add when it resolves a claim. */
export const MAX_ALLOWED_ORDER_TAGS = 20;

/** Escalation ceiling (store currency) shown for a claim type with no store-configured threshold. */
export const DEFAULT_ESCALATION_AMOUNT = 350;

/**
 * Claim queues an escalation tier can key off, as `Claims.ClaimState` values with their queue
 * labels. To Do is unworked claims; On Hold is escalations parked for a human.
 */
export const ESCALATION_TIER_QUEUES = [
  { key: 'NEW', label: 'To Do' },
  { key: 'OPEN', label: 'On Hold' },
] as const;

export type EscalationTierQueue = (typeof ESCALATION_TIER_QUEUES)[number]['key'];

export const DEFAULT_ESCALATION_TIER_QUEUE: EscalationTierQueue = 'NEW';

/**
 * Queue-level automation state filters accepted by the claims list/counts
 * endpoints (`?automation=`). Drives the To Do queue's automation filter;
 * always send the same value to BOTH endpoints — the frontend derives
 * pagination totals from counts.
 */
export const CLAIM_AUTOMATION_FILTERS = [
  'queued',
  'processing',
  'ready',
  'escalated',
  'paused',
  'stale_takeover',
] as const;

export type ClaimAutomationFilter = (typeof CLAIM_AUTOMATION_FILTERS)[number];

const DELAYABLE_KEYS: ReadonlySet<string> = new Set(DELAYABLE_AUTOMATION_ACTIONS.map(a => a.key));

export function isDelayableAutomationAction(toolName: string): toolName is DelayableAutomationAction {
  return DELAYABLE_KEYS.has(toolName);
}

/** Close reasons a `resolve_claim` CLOSE may carry; NO_RESPONSE is human-only (the inactivity timer writes no record). */
export const CLOSE_RESOLUTION_TYPES = [
  ResolutionType.PACKAGE_ARRIVED,
  ResolutionType.OTHER,
  ResolutionType.NOT_MERCHANT_PRODUCT,
] as const;

export type CloseResolutionType = (typeof CLOSE_RESOLUTION_TYPES)[number];

const CLOSE_RESOLUTION_TYPE_SET: ReadonlySet<string> = new Set(CLOSE_RESOLUTION_TYPES);

export function isCloseResolutionType(value: unknown): value is CloseResolutionType {
  return typeof value === 'string' && CLOSE_RESOLUTION_TYPE_SET.has(value);
}

/** On-demand instruction files the automation agent loads with `get_instruction`: name → brand-ai-agents path. */
export const AUTOMATION_INSTRUCTIONS = {
  'police-report': 'global/instructions/police-report.md',
  'fraud-analysis': 'global/instructions/fraud-analysis.md',
  'image-fraud': 'global/instructions/image-fraud.md',
} as const;

export type AutomationInstructionName = keyof typeof AUTOMATION_INSTRUCTIONS;

export const AUTOMATION_INSTRUCTION_NAMES = Object.keys(AUTOMATION_INSTRUCTIONS) as [
  AutomationInstructionName,
  ...AutomationInstructionName[],
];

export function isAutomationInstructionName(value: unknown): value is AutomationInstructionName {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(AUTOMATION_INSTRUCTIONS, value);
}
